import { memo, type ReactNode } from "react";
import {
  Alert,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";

interface ActivitySectionFrameProps {
  title: string;
  subtitle?: string;
  isLoading: boolean;
  hasError: boolean;
  errorText: string;
  isEmpty: boolean;
  emptyText: string;
  onRefresh: () => void;
  /** Opcional: filtros (ex.: datas) logo abaixo do título. */
  toolbar?: ReactNode;
  /** Opcional: ação principal no cabeçalho, ao lado do atualizar (ex.: "Marcar"). */
  action?: ReactNode;
  /** Opcional: ação logo abaixo do texto do estado vazio (ex.: o mesmo "Marcar"). */
  emptyAction?: ReactNode;
  children: ReactNode;
}

/**
 * Moldura das listas da prestadora: título, atualizar e os estados
 * carregando / erro / vazio. A lista em si vem por `children`.
 */
export const ActivitySectionFrame = memo(function ActivitySectionFrame({
  title,
  subtitle,
  isLoading,
  hasError,
  errorText,
  isEmpty,
  emptyText,
  onRefresh,
  toolbar,
  action,
  emptyAction,
  children,
}: ActivitySectionFrameProps) {
  const renderContent = () => {
    if (isLoading && isEmpty) {
      return (
        <Stack alignItems="center" py={3}>
          <CircularProgress size={26} />
        </Stack>
      );
    }
    if (hasError) return <Alert severity="error">{errorText}</Alert>;
    if (isEmpty) {
      return (
        <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
          {emptyAction && (
            <Stack alignItems="center" sx={{ mt: 1.5 }}>
              {emptyAction}
            </Stack>
          )}
        </Paper>
      );
    }
    return (
      <Stack spacing={1} role="list">
        {children}
      </Stack>
    );
  };

  return (
    <Stack spacing={1.5} width="100%">
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          {action}
          <Tooltip title="Atualizar">
            <span>
              <IconButton onClick={onRefresh} disabled={isLoading} aria-label={`Atualizar ${title}`}>
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
      {toolbar}
      {renderContent()}
    </Stack>
  );
});
